import { Flex, Segmented, Typography } from 'antd';
import type { CSSProperties, ReactElement } from 'react';
import useBreakpoint from '@/hooks/useBreakpoint';
import YearLimitLabel from './YearLimitLabel';

interface BreakdownFieldProps<TValue extends string> {
  label: string;
  options: { label: string; value: TValue; disabled?: boolean }[];
  value: TValue;
  onChange: (value: TValue) => void;
  style?: CSSProperties;
}

/**
 * A labelled single-choice control. The options stack vertically on mobile. Disabled options
 * explain in a tooltip that they need a date range of one year or less.
 */
const BreakdownField = <TValue extends string>({
  label,
  options,
  value,
  onChange,
  style,
}: BreakdownFieldProps<TValue>): ReactElement => {
  const { isMobile } = useBreakpoint();

  return (
    <Flex vertical gap={16} style={style}>
      <Typography.Text type="secondary" style={{ fontSize: 16 }}>
        {label}
      </Typography.Text>
      <Segmented
        name={label}
        block
        vertical={isMobile}
        size="large"
        options={options.map((option) => ({
          ...option,
          label: <YearLimitLabel label={option.label} disabled={Boolean(option.disabled)} />,
        }))}
        value={value}
        onChange={onChange}
      />
    </Flex>
  );
};

export default BreakdownField;
